import { Component, type PropsWithChildren } from 'react';

import { onLocationChange } from 'src/utils/dom';

import ErrorPage from './ErrorPage';

interface ErrorWithExtraMaybe extends Error {
  extra?: {
    errorBoundaryInfo?: {
      componentStack: string;
    };
  };
}

interface ErrorBoundaryState {
  error: Error | undefined;
}

class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
  private locationChangeInterval: ReturnType<typeof setInterval> | undefined =
    undefined;

  state = {
    error: undefined,
  };

  constructor(props: PropsWithChildren) {
    super(props);
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentWillUnmount() {
    clearInterval(this.locationChangeInterval);
  }

  componentDidCatch(
    error: ErrorWithExtraMaybe,
    info: { componentStack: string }
  ) {
    if (this.locationChangeInterval) {
      clearInterval(this.locationChangeInterval);
    }

    this.locationChangeInterval = onLocationChange(this.handleLocationChange);

    error.extra = {
      ...(error.extra || {}),
      errorBoundaryInfo: info,
    };

    // TODO: consolidate logging the error to datadog here
    this.setState({ error });
  }

  private handleLocationChange = () => {
    this.setState({ error: undefined });
  };

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    // INFO: we could handle different error types here based on the error message, etc.
    return <ErrorPage error={this.state.error} />;
  }
}

export default ErrorBoundary;
